import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Typography,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Paper,
  Box,
  NativeSelect,
  Radio,
  FormControlLabel,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { actions, selectors } from '../../store/dux/cornrx-dux';
import { selectors as growerSelectors } from '../../store/dux/grower-dux';
import { selectors as fieldSelectors } from '../../store/dux/fields-dux';
import { useCurrentUser } from '../../hooks/use-current-user';
import { runCornRX } from '../../lib/api';
import { Spinner } from '../../components/Spinner';
import { useAlerts } from '../../hooks/use-alerts';

export default function CornRXRun(props) {
  const { formopen } = props;
  const dispatch = useDispatch();
  const { showInfo, showError } = useAlerts();
  const [isRunning, setRunning] = useState(false);
  const [rxType, setRxType] = useState('');
  const [selectedCoefficient, setSelectedCoefficient] = useState('');
  const growerId = useSelector(growerSelectors.selectedId);
  const aaids = useSelector(fieldSelectors.selectedAaids);
  const coefficients = useSelector(selectors.coefficient)?.Coefficient;
  const isPreviousCoefficient = useSelector(selectors.coefficient)?.previous;
  const currentUser = useCurrentUser();
  const isAdministrator = currentUser.userType === 'administrator';

  useEffect(() => {
    dispatch(actions.loadCoefficient(aaids[0], growerId));
  }, [aaids, dispatch, growerId]);

  const handleCoefficientChange = useCallback(
    (event, CoefModelKey) => {
      const newCoefficient = coefficients[selectedCoefficient].map((coef) => {
        if (coef.rule === CoefModelKey.model.rule) {
          return { ...coef, [CoefModelKey.key]: event.target.value };
        }
        return coef;
      });

      dispatch(
        actions.setCoefficient({
          Coefficient: {
            ...coefficients,
            [selectedCoefficient]: newCoefficient,
          },
          previous: isPreviousCoefficient,
        }),
      );
    },
    [coefficients, dispatch, isPreviousCoefficient, selectedCoefficient],
  );

  const handleClose = useCallback(() => {
    formopen(false);
  }, [formopen]);

  const handleRun = useCallback(async () => {
    try {
      setRunning(true);
      const rxParams = { rxType, coefficients };
      const jobs = aaids.map((aaid) => runCornRX(aaid, rxParams));
      await Promise.all(jobs);
      showInfo(`Rx job${jobs.length > 1 ? 's' : ''} has been queued`);
      dispatch(actions.loadGrowerHasCornRX(growerId));
      handleClose();
    } catch (e) {
      showError(e);
    } finally {
      setRunning(false);
    }
  }, [
    aaids,
    coefficients,
    dispatch,
    growerId,
    handleClose,
    rxType,
    showError,
    showInfo,
  ]);

  const handleFocus = (event) => event.target.select();

  return coefficients ? buildForm() : <Spinner sx={{ height: 200 }} />;

  function buildForm() {
    return (
      <>
        {buildRxType()}
        {buildCoefficients()}
        {buildActions()}
      </>
    );
  }

  function buildActions() {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          marginTop: 1,
          width: '100%',
          justifyContent: 'flex-end',
        }}
      >
        <Button color="inherit" variant="contained" onClick={handleClose}>
          Close
        </Button>
        <LoadingButton
          loading={isRunning}
          variant="contained"
          onClick={handleRun}
        >
          Generate
        </LoadingButton>
      </Box>
    );
  }

  function buildRxType() {
    return (
      <>
        <Typography sx={{ mb: 0.5 }}>Override Rx type</Typography>
        <Box sx={{ mb: 2 }}>
          {[
            { label: 'No Override', value: '' },
            { label: 'Soil', value: 'Soil' },
            { label: 'SWI', value: 'SWI' },
          ].map((option) => (
            <FormControlLabel
              checked={option.value === rxType}
              onChange={(e) => setRxType(e.target.value)}
              control={<Radio />}
              label={option.label}
              value={option.value}
            />
          ))}
        </Box>
      </>
    );
  }

  function buildCoefficients() {
    const vals = selectedCoefficient ? coefficients[selectedCoefficient] : [];
    const keys = Object.keys(coefficients);
    const coefficientsType = isPreviousCoefficient
      ? `Coefficients from previous execution`
      : `AA Standard Coefficients`;

    return (
      <>
        <Typography sx={{ mb: 0.5 }}>{coefficientsType}</Typography>
        <FormControl variant="standard">
          <Box sx={{ display: isAdministrator ? 'block' : 'none' }}>
            <InputLabel>Coef Values</InputLabel>
            <NativeSelect
              onChange={(e) => setSelectedCoefficient(e.target.value)}
            >
              <option value="" />
              {keys.map((k) => (
                <option value={k} key={k}>
                  {' '}
                  {k}{' '}
                </option>
              ))}
            </NativeSelect>
          </Box>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {vals.map((vx) => (
              <Paper style={{ marginTop: 10, width: '100%' }}>
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    gap: 2,
                  }}
                >
                  <div
                    style={{
                      marginLeft: 10,
                      marginTop: 25,
                      marginRight: 10,
                      textAlign: 'center',
                    }}
                  >
                    {`Rule ${vx.rule} :`}
                  </div>
                  {Object.keys(vx)
                    .filter((f) => f !== 'rule')
                    .map((mv) => (
                      <TextField
                        variant="standard"
                        sx={{ flex: 1 }}
                        id="outlined-number"
                        key={mv}
                        label={mv}
                        value={vx[mv]}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        margin="normal"
                        onChange={(e) =>
                          handleCoefficientChange(e, { model: vx, key: mv })
                        }
                        onFocus={handleFocus}
                      />
                    ))}
                </Box>
              </Paper>
            ))}
          </div>
        </FormControl>
      </>
    );
  }
}
